/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { canShowDiscount } from '@core/Utils/Vehicle/CanShowDiscount';
import { Logo } from '@gds/Logo/Logo';
import { TotalPrice } from '@gds/TotalPrice/TotalPrice';
import { Typography } from '@gds/Typography/Typography';
import { AdminFee } from '@shared-components/AdminFee/AdminFee';
import PriceRatingGauge from '@vehicle-tile/Components/PriceRatingGauge/PriceRatingGauge';

import UspPills from 'Components/Pdp/UspPills/UspPills';

import { FinanceInfoTooltip } from './FinanceInfoTooltip/FinanceInfoTooltip';
import { FinancingDetails } from './FinancingDetails/FinancingDetails';
import type { VehicleAttributesProps } from './VehicleAttributes.entity';

import styles from './VehicleAttributes.module.css';
export const VehicleAttributes = ({
  locale,
  vehicle,
  hasSmallPrint,
  strings,
  pageType,
  index,
  hasCampaignDiscount,
  usps = null,
  showUspPills,
  priceRatingStrings,
  infoType,
  isStockUnavailable,
}: VehicleAttributesProps) => {
  const {
    prettyName,
    variant,
    pricing,
    spec,
    ecom: { isEcom },
  } = vehicle;

  const financeRate = vehicle.finance?.rate;

  const shortDisclaimer = vehicle.finance?.legalText?.shortDisclaimer;
  const showShortDisclaimer = hasSmallPrint && locale === 'fr' && shortDisclaimer;
  const showLenderNote = financeRate !== undefined && locale === 'uk';

  const showDiscount = canShowDiscount(pricing);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const tenant = useAtomValue(tenantAtom);

  const validPriceRating =
    locale !== 'fr'
      ? vehicle.pricing.priceRating !== 'high'
      : vehicle.pricing.priceRating !== 'fair' && vehicle.pricing.priceRating !== 'high';

  const canShowGauge =
    ((infoType === 'stickyInfo' && locale === 'fr') || locale === 'uk') &&
    vehicle.pricing.priceRating &&
    validPriceRating;

  const canShowVspDisclaimer = locale === 'uk' && tenant !== 'rac' && spec.d2c && isEcom;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80 && window.innerWidth >= 1024) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles.attributeWrapperSidebar}>
      <div className={styles.makeModelNameWrapper}>
        <Typography
          variant={isScrolling ? 'h6' : 'h4'}
          className={styles.vehiclePrettyName}
          dataTestId="vehiclePrettyName"
        >
          {prettyName}
        </Typography>
        <Typography className={styles.vehicleVariantName} dataTestId="vehicleVariantName">
          {variant?.label || spec.display}
        </Typography>
      </div>
      {showUspPills && !!usps && <UspPills usps={usps} />}
      <div
        className={`${styles.scrollWrapper} ${
          showDiscount && isScrolling ? styles.discountAndScrolled : ''
        }`}
        data-scrolled={!showDiscount && isScrolling}
      >
        <div className={styles.priceWrapper} data-variant={'default'}>
          <div className={styles.priceAttributes}>
            <div className={styles.inlineVAT}>
              <TotalPrice
                locale={locale}
                pricing={pricing}
                strings={strings}
                isScrolling={isScrolling}
                hasCampaignDiscount={hasCampaignDiscount}
                variant={'default'}
                shallShowVatText={false}
              />
              {vehicle.pricing?.isVatAdded && (
                <Typography Component="span" variant="caption2">
                  {strings.vatIncluded}
                </Typography>
              )}
            </div>
            {canShowGauge && (
              <PriceRatingGauge
                locale={locale}
                rating={vehicle.pricing.priceRating!}
                strings={priceRatingStrings}
                clickable={true}
              />
            )}
          </div>
          <AdminFee vehicle={vehicle} source="pdp" />
          {vehicle.pricing?.hasFinance && (
            <Typography className={styles.financeDetails} data-variant={'default'}>
              <FinancingDetails
                strings={strings}
                locale={locale}
                vehicle={vehicle}
                pageType={pageType}
                index={index}
                isStockUnavailable={isStockUnavailable}
                isFinanceFirst={false}
                isScrolling={isScrolling}
                hasDiscount={!!showDiscount}
              />
              {locale === 'fr' && vehicle.finance?.legalText?.fullDisclaimer && (
                <FinanceInfoTooltip
                  disclaimer={vehicle.finance?.legalText?.fullDisclaimer}
                  title={strings.financeModalTitle}
                />
              )}
            </Typography>
          )}
        </div>
      </div>
      {showLenderNote && (
        <Typography Component="span" variant={canShowVspDisclaimer ? 'body4' : 'body2'}>
          {strings.lenderNote}
        </Typography>
      )}

      {canShowVspDisclaimer && (
        <div className={styles.contact}>
          <Typography Component="span" variant="body2">
            {strings.investorsStockQuestion}
          </Typography>
          <Typography Component="span" variant="body4" className={styles.logo}>
            {strings.investorsStockContactHeycar} <Logo usage="light_bg" fontSize={80} />
          </Typography>
        </div>
      )}
      {showShortDisclaimer && (
        <Typography Component="span" variant="caption5" className={styles.smallPrint}>
          <span dangerouslySetInnerHTML={{ __html: shortDisclaimer }} />
        </Typography>
      )}
    </div>
  );
};
